<template>
    <SettingsCard class="ds-settings-block ds-role-card">
        <SettingsBlockState
            :status="editor.load.roles"
            :rows="8"
            error-text="Не удалось загрузить роли."
            @retry="editor.loadRoles()"
        >
            <div v-if="!role" class="ds-settings-empty">Список ролей пуст.</div>
            <template v-else>
                <header class="ds-role-card-header">
                    <h2 class="ds-role-card-title">{{ role.name }}</h2>
                    <span class="ds-settings-muted ds-settings-nowrap">включено {{ enabledCount }} из {{ ROLE_PERMISSIONS.length }}</span>
                </header>

                <div class="ds-role-toolbar">
                    <label class="ds-settings-search-field ds-role-search">
                        <SettingsIcon name="search" :size="16" :stroke-width="2" />
                        <input v-model="editor.roleView.query" type="search" placeholder="Право или ключ" aria-label="Право или ключ" autocomplete="off">
                    </label>
                    <div class="ds-glass-segment ds-role-segment" role="radiogroup" aria-label="Фильтр прав">
                        <button
                            v-for="option in filters"
                            :key="option.value"
                            type="button"
                            role="radio"
                            class="ds-glass-segment-option"
                            :class="{ 'is-active': editor.roleView.filter === option.value }"
                            :aria-checked="editor.roleView.filter === option.value"
                            @click="editor.roleView.filter = option.value"
                        >{{ option.label }}</button>
                    </div>
                </div>

                <div class="ds-role-table" role="table" :aria-label="`Права роли ${role.name}`">
                    <div class="ds-role-row ds-settings-param-head" role="row">
                        <span role="columnheader">Право</span>
                        <span role="columnheader">Ключ</span>
                        <span role="columnheader" class="ds-role-access-head">Доступ</span>
                    </div>

                    <div v-if="!visibleGroups.length" class="ds-settings-empty">Ничего не найдено</div>

                    <div v-for="group in visibleGroups" :key="group.id" role="rowgroup" class="ds-role-group">
                        <div class="ds-role-row ds-role-group-row" role="row">
                            <span role="cell" class="ds-role-group-cell">
                                <button
                                    type="button"
                                    class="ds-role-group-toggle"
                                    :aria-expanded="isExpanded(group.id)"
                                    :aria-controls="`role-group-${group.id}`"
                                    @click="toggleGroup(group.id)"
                                >
                                    <SettingsIcon name="chevron" :size="14" :stroke-width="2" class="ds-role-chevron" :class="{ 'is-collapsed': !isExpanded(group.id) }" />
                                    <span>{{ group.label }}</span>
                                    <span class="ds-role-group-count">{{ groupEnabled(group) }}/{{ group.permissions.length }}</span>
                                </button>
                            </span>
                            <span role="cell"></span>
                            <span role="cell" class="ds-role-access">
                                <BooleanSwitch
                                    :model-value="groupEnabled(group) === group.permissions.length"
                                    :disabled="editor.saving.value"
                                    @update:model-value="toggleWholeGroup(group)"
                                ><span class="sr-only">Все права группы «{{ group.label }}»</span></BooleanSwitch>
                            </span>
                        </div>

                        <div v-show="isExpanded(group.id)" :id="`role-group-${group.id}`">
                            <template v-for="permission in group.visible" :key="permission.key">
                                <div
                                    :id="`permission-${permission.key}`"
                                    class="ds-role-row ds-role-permission-row"
                                    :class="rowClass(`permission-${permission.key}`)"
                                    role="row"
                                >
                                    <span role="cell" class="ds-role-permission-label">{{ permission.label }}</span>
                                    <span role="cell" class="ds-role-key">{{ permission.key }}</span>
                                    <span role="cell" class="ds-role-access" :class="{ 'is-changed': changedKeys.has(permission.key) }">
                                        <BooleanSwitch
                                            :model-value="editor.roleValue(role.id, permission.key)"
                                            :disabled="editor.saving.value || isHardLocked(permission.key)"
                                            :title="isHardLocked(permission.key) ? lockReason(permission.key) : undefined"
                                            @update:model-value="editor.setRolePermissionValues(role.id, { [permission.key]: $event })"
                                        ><span class="sr-only">{{ permission.label }}</span></BooleanSwitch>
                                    </span>
                                </div>
                                <div
                                    v-if="permission.key === 'can_edit_past_fact_time' && showDays"
                                    :id="`permission-${ROLE_DAYS_KEY}`"
                                    class="ds-role-row ds-role-permission-row"
                                    :class="rowClass(`permission-${ROLE_DAYS_KEY}`)"
                                    role="row"
                                >
                                    <span role="cell" class="ds-role-permission-label">
                                        Дней в прошлом для редактирования факта
                                        <span class="ds-settings-muted ds-role-days-hint">
                                            {{ daysLocked ? 'Доступно при включённом праве «Редактирование фактического времени в прошлом»' : 'пусто — глобальное значение' }}
                                        </span>
                                    </span>
                                    <span role="cell" class="ds-role-key">{{ ROLE_DAYS_KEY }}</span>
                                    <span role="cell" class="ds-role-access ds-role-days">
                                        <SettingsNumberField
                                            :model-value="editor.roleDaysText(role.id)"
                                            label="Дней в прошлом для редактирования факта"
                                            unit="дн"
                                            :changed="changedKeys.has(ROLE_DAYS_KEY)"
                                            :invalid="daysInvalid"
                                            error-text="Целое число не меньше 0 или пусто"
                                            :disabled="editor.saving.value || daysLocked"
                                            @update:model-value="editor.setRoleDays(role.id, $event)"
                                        />
                                    </span>
                                </div>
                            </template>
                        </div>
                    </div>
                </div>
            </template>
        </SettingsBlockState>
    </SettingsCard>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import SettingsCard from '@/ui/SettingsCard.vue'
import BooleanSwitch from '@/ui/BooleanSwitch.vue'
import SettingsBlockState from '@/components/settings/SettingsBlockState.vue'
import SettingsIcon from '@/components/settings/SettingsIcon.vue'
import SettingsNumberField from '@/components/settings/SettingsNumberField.vue'
import { useSettingsEditor, type RoleFilter } from '@/components/settings/useSettingsEditor'
import {
    ROLE_DAYS_KEY,
    ROLE_DEPENDENCIES,
    ROLE_GROUPS,
    ROLE_PERMISSIONS,
    parseRoleDays,
    type RoleBooleanKey,
    type RoleGroup,
    type RolePermission,
} from '@/components/settings/settingsModel'

// RD-163: таблица прав выбранной роли. Поиск и фильтр меняют только видимость:
// счётчики и групповой переключатель работают на полном наборе прав.

const editor = useSettingsEditor()

const filters: Array<{ value: RoleFilter, label: string }> = [
    { value: 'all', label: 'Все' },
    { value: 'on', label: 'Вкл.' },
    { value: 'off', label: 'Выкл.' },
    { value: 'changed', label: 'Изменённые' },
]

const role = computed(() => editor.selectedRole.value)
const changes = computed(() => (role.value ? editor.roleChangeList(role.value.id) : []))
const changedKeys = computed(() => new Set(changes.value.map((change) => change.techKey).filter(Boolean) as string[]))

const valueOf = (key: RoleBooleanKey) => (role.value ? editor.roleValue(role.value.id, key) : false)
const enabledCount = computed(() => ROLE_PERMISSIONS.filter((permission) => valueOf(permission.key)).length)
const groupEnabled = (group: RoleGroup) => group.permissions.filter((permission) => valueOf(permission.key)).length

const daysAnchor = `permission-${ROLE_DAYS_KEY}`
// Строка числового параметра зависит от права; переход из поиска показывает её и при выключенном праве
// (поле заблокировано, право автоматически не включается).
const daysLocked = computed(() => !valueOf('can_edit_past_fact_time'))
const showDays = computed(() => !daysLocked.value
    || changedKeys.value.has(ROLE_DAYS_KEY)
    || editor.activeAnchor.value === daysAnchor)
const daysInvalid = computed(() => !parseRoleDays(role.value ? editor.roleDaysText(role.value.id) : '').valid)

const isFiltering = computed(() => Boolean(editor.roleView.query.trim()) || editor.roleView.filter !== 'all')

function matches(permission: RolePermission) {
    const query = editor.roleView.query.trim().toLocaleLowerCase('ru')
    const daysRow = permission.key === 'can_edit_past_fact_time' && showDays.value
    const text = `${permission.label} ${permission.key}${daysRow ? ` дней в прошлом для редактирования факта ${ROLE_DAYS_KEY}` : ''}`.toLocaleLowerCase('ru')
    if (query && !text.includes(query)) return false
    const filter = editor.roleView.filter
    if (filter === 'on') return valueOf(permission.key)
    if (filter === 'off') return !valueOf(permission.key)
    if (filter === 'changed') return changedKeys.value.has(permission.key) || (daysRow && changedKeys.value.has(ROLE_DAYS_KEY))
    return true
}

const visibleGroups = computed(() => ROLE_GROUPS
    .map((group) => ({ ...group, visible: isFiltering.value ? group.permissions.filter(matches) : group.permissions }))
    .filter((group) => group.visible.length > 0))

// По умолчанию раскрыта только первая группа; явный выбор пользователя хранится в roleView.collapsed.
const isCollapsed = (groupId: string) => editor.roleView.collapsed[groupId] ?? groupId !== ROLE_GROUPS[0]?.id
const isExpanded = (groupId: string) => isFiltering.value || !isCollapsed(groupId)
const toggleGroup = (groupId: string) => {
    editor.roleView.collapsed[groupId] = isExpanded(groupId)
}

function toggleWholeGroup(group: RoleGroup) {
    if (!role.value) return
    // Смешанное состояние включает группу, полностью включённая — выключается.
    const enable = groupEnabled(group) !== group.permissions.length
    const updates = Object.fromEntries(group.permissions.map((permission) => [permission.key, enable])) as Partial<Record<RoleBooleanKey, boolean>>
    editor.setRolePermissionValues(role.value.id, updates)
}

const hardParent = (key: RoleBooleanKey) => ROLE_DEPENDENCIES.find((dependency) => dependency.hard && dependency.children.includes(key))?.parent
const isHardLocked = (key: RoleBooleanKey) => {
    const parent = hardParent(key)
    return Boolean(parent && !valueOf(parent))
}
const lockReason = (key: RoleBooleanKey) => {
    const parent = hardParent(key)
    const label = ROLE_PERMISSIONS.find((permission) => permission.key === parent)?.label
    return label ? `Доступно при включённом праве «${label}»` : undefined
}

const rowClass = (anchor: string) => ({
    'is-changed': editor.changedAnchors.value.has(anchor),
    'is-highlighted': editor.highlightedAnchor.value === anchor,
})
</script>
